import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import UsersTable from "@/pages/admin-users/UsersTable";
import ResetPasswordDialog from "@/pages/admin-users/ResetPasswordDialog";
import ChangeRoleDialog from "@/pages/admin-users/ChangeRoleDialog";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [roleTarget, setRoleTarget] = useState(null);
  const [roleValue, setRoleValue] = useState("user");

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onError = (e) => toast.error(e.response?.data?.detail || "Failed");

  const resetPw = async () => {
    if (pwValue.length < 6) { toast.error("Min 6 characters"); return; }
    try {
      await api.post(`/admin/users/${pwTarget.id}/reset-password`, { new_password: pwValue });
      toast.success(`Password reset for ${pwTarget.email}`);
      setPwTarget(null); setPwValue("");
    } catch (e) { onError(e); }
  };

  const changeRole = async () => {
    try {
      await api.patch(`/admin/users/${roleTarget.id}/role`, { role: roleValue });
      toast.success(`Role updated to ${roleValue}`);
      setRoleTarget(null);
      load();
    } catch (e) { onError(e); }
  };

  const toggleLifetime = async (u) => {
    const granting = u.subscription_status !== "lifetime";
    if (!window.confirm(
      granting
        ? `Grant LIFETIME access to ${u.email}? They'll have full premium forever, at no charge.`
        : `Revoke lifetime access from ${u.email}? They'll fall back to their trial/subscription status.`
    )) return;
    try {
      await api.patch(`/admin/users/${u.id}/lifetime`, { is_lifetime: granting });
      toast.success(granting ? `Lifetime granted to ${u.email}` : `Lifetime revoked from ${u.email}`);
      load();
    } catch (e) { onError(e); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { onError(e); }
  };

  const reconcile = async (u) => {
    if (!window.confirm(
      `Force-fulfil any unfulfilled Stripe checkout for ${u.email}?\n\n` +
      "Use this when a customer paid but their badge still says TRIAL. " +
      "Safe to re-run — only marks paid sessions as fulfilled."
    )) return;
    try {
      const { data } = await api.post(
        `/admin/billing/reconcile-user/${encodeURIComponent(u.email.toLowerCase())}`
      );
      if (data.reconciled_any) {
        toast.success(`Reconciled ${u.email}. Subscription now active.`);
        load();
      } else {
        const reasons = (data.outcomes || [])
          .map((o) => o.reason || o.stripe_payment_status || "(no detail)")
          .join(" · ") || "No paid sessions on Stripe.";
        toast.warning(`Nothing to reconcile for ${u.email}: ${reasons}`, { duration: 12000 });
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Reconcile failed.";
      toast.error(`Reconcile error: ${detail}`, { duration: 12000 });
    }
  };

  return (
    <div className="max-w-7xl">
      <div className="gf-overline">Admin · Users</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
        User management.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl text-sm leading-relaxed">
        Manage all platform users — reset passwords, toggle admin role, or remove accounts.
      </p>

      <UsersTable
        users={users}
        meId={me?.id}
        onResetPw={(u) => { setPwTarget(u); setPwValue(""); }}
        onChangeRole={(u) => { setRoleTarget(u); setRoleValue(u.role === "admin" ? "user" : "admin"); }}
        onDelete={del}
        onReconcile={reconcile}
        onToggleLifetime={toggleLifetime}
      />

      <ResetPasswordDialog
        target={pwTarget}
        value={pwValue}
        setValue={setPwValue}
        onClose={() => setPwTarget(null)}
        onConfirm={resetPw}
      />

      <ChangeRoleDialog
        target={roleTarget}
        value={roleValue}
        setValue={setRoleValue}
        onClose={() => setRoleTarget(null)}
        onConfirm={changeRole}
      />
    </div>
  );
}
